/**
 * trophiesMapping.js — the Trophies screen and Today's streak, from what the server actually said
 * (#583/#584 N1 slice S7; design D3, D4, D5, D9, tests T7–T14 and T23).
 *
 * | read | shape |
 * | --- | --- |
 * | `GET /api/trophies/:userId` (S6) | `{ enabled: false }` while `story_credit_rollout` is off; otherwise catalog, badges with `basis` or `null`, goals with versions and candles, `streak`, `xp`, `coverage`, `asOf` |
 * | `GET /api/streaks/:userId` (S3) | `{ verified: false, legacy }` while `streak_rollout` is off; `{ available: false }` when the worker has not reached the member; otherwise 28 day states, run, freezes, finalized-through |
 * | `GET /api/achievements`, `/api/achievements/user?userId=`, `/api/goals/progress?userId=` | the badge catalog, earned badges and goal progress that exist today, with no receipts |
 * | `GET /api/story-progress/:userId` (S13) | `{ enabled: false }` while off; otherwise verified `xp`, the Q3 `level`, its `policy` version and `coverage` |
 *
 * **Dates are household dates, never the browser's.** Every day the streak read names is a
 * `YYYY-MM-DD` string in the household's zone. `new Date('2026-09-26')` is UTC midnight, which a
 * browser west of Greenwich prints as Sep 25, so these helpers never hand such a string to a
 * zone-aware formatter: they read its parts and format at UTC.
 *
 * **Unknown is never zero.** A streak the server calls stale, pending, blocked or unavailable has no
 * number here: `header` is null and the sentence says "not current". A missing field is a malformed
 * answer, and every function returns `null` for one, so the screen says it could not read it.
 *
 * **No money arithmetic.** The story payload carries no currency, so a reward target is shown as the
 * number the server stored, labelled with its basis, never formatted as a currency it did not name.
 * The off state's reward goals are money only when the household read (`GET /api/tenants/current`)
 * named the currency.
 *
 * **Whose clock.** An instant is shown in the streak policy's zone, else the household read's zone,
 * else labelled UTC. Never the browser's.
 */

import { badgeArt } from '../assets.js';
import { GOAL_TYPE_LABELS, QUEST_COUNT_GOAL_TYPES } from './familyMapping.js';

const isObject = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);
const LOCAL_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

/** `YYYY-MM-DD` → its UTC-midnight Date, or null. Only ever formatted at UTC. */
function partsOf(localDate) {
  const m = typeof localDate === 'string' ? LOCAL_DATE.exec(localDate) : null;
  if (!m) return null;
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  return d.toISOString().slice(0, 10) === localDate ? d : null;
}

/** A household date as "Sep 26", whatever zone this browser is in. */
export function localDateLabel(localDate) {
  const d = partsOf(localDate);
  return d ? new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' }).format(d) : null;
}

/** A zone name this runtime can format in, or null. Nothing unvalidated reaches a sentence. */
function validZone(timeZone) {
  if (typeof timeZone !== 'string' || timeZone === '') return null;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone }).format(0);
    return timeZone;
  } catch {
    return null;
  }
}

const readableInstant = (iso) => typeof iso === 'string' && !Number.isNaN(new Date(iso).getTime());

/** An instant in the household's zone, "Sep 26, 7:05 PM"; with no known zone it says UTC. */
export function instantLabel(iso, timeZone, { time = true } = {}) {
  const d = typeof iso === 'string' ? new Date(iso) : null;
  if (!d || Number.isNaN(d.getTime())) return null;
  const opts = time ? { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' } : { month: 'short', day: 'numeric' };
  const zone = validZone(timeZone);
  if (zone) return new Intl.DateTimeFormat('en-US', { ...opts, timeZone: zone }).format(d);
  return `${new Intl.DateTimeFormat('en-US', { ...opts, timeZone: 'UTC' }).format(d)} UTC`;
}

/** The zone every instant on the screen uses: the streak policy's, else the household read's. */
export function screenZone(streak, household) {
  const policyZone = isObject(streak) && isObject(streak.policy) ? validZone(streak.policy.timezone) : null;
  return policyZone ?? (isObject(household) ? validZone(household.timezone) : null);
}

/** A goal type in the family's words (the Family screen's labels); an unknown type verbatim. */
const goalTypeTitle = (goalType) => GOAL_TYPE_LABELS[goalType] ?? String(goalType ?? 'Goal').replace(/_/g, ' ');

/** Monday = 0 … Sunday = 6, for a household date. */
const weekdayOf = (d) => (d.getUTCDay() + 6) % 7;
const shift = (d, days) => new Date(d.getTime() + days * 86400000); // UTC midnights: no DST here

/**
 * The calendar's "today, lit so far" (D3, D5), and the one rule Today's sentence and the Trophies
 * calendar both read (#889): an open day, on the household's own today (the streak read's `today`,
 * never the browser's clock), holding a saved quest. The reader opens a day only on that date
 * (`streakRead.service.js` getStreak), so an open day on any other date is not today.
 */
export function isTodayLitSoFar(day, householdToday) {
  return isObject(day) && day.state === 'open' && partsOf(householdToday) !== null
    && day.date === householdToday && Number(day.qualifyingCount) > 0;
}

/** One server day state → a StreakHistory dot, with a title that says what it is. */
function dotFor(day, householdToday) {
  const label = localDateLabel(day.date);
  switch (day.state) {
    case 'lit': return { state: 'lit', title: `${label}: lit`, kind: 'lit' };
    case 'rest': return { state: 'quiet', title: `${label}: rest day`, kind: 'rest' };
    case 'freeze': return { state: 'freeze', title: `${label}: a freeze kept the run`, kind: 'freeze' };
    case 'quiet': return { state: 'quiet', title: `${label}: quiet`, kind: 'quiet' };
    case 'open':
      if (isTodayLitSoFar(day, householdToday)) return { state: 'ember', title: `${label}: today, a quest saved so far`, kind: 'open' };
      return day.date === householdToday
        ? { state: 'future', title: `${label}: today, still open`, kind: 'open' }
        : { state: 'future', title: `${label}: not closed yet`, kind: 'pending' };
    case 'pending': return { state: 'future', title: `${label}: not closed yet`, kind: 'pending' };
    default: return null;
  }
}

const LEGEND = Object.freeze([
  { state: 'lit', label: 'day lit' },
  { state: 'ember', label: 'today, lit so far' },
  { state: 'freeze', label: 'a freeze kept the run' },
  { state: 'quiet', label: 'quiet or rest day' },
  { state: 'future', label: 'still open, not closed yet, or outside these four weeks' },
]);

/** The 28 days as Monday-first weeks, each with a summary built from its REAL days only. */
function toWeeks(days, householdToday) {
  if (days.length > 28) return null;
  const dots = [];
  for (const day of days) {
    const d = isObject(day) ? partsOf(day.date) : null;
    const dot = d ? dotFor(day, householdToday) : null;
    if (!dot) return null;
    dots.push({ d, dot });
  }
  if (dots.length === 0) return [];
  // Strictly ascending, or it is not the reader's answer (and the week walk below would never end).
  for (let k = 1; k < dots.length; k += 1) {
    if (dots[k].d.getTime() - dots[k - 1].d.getTime() !== 86400000) return null;
  }
  const weeks = [];
  let monday = shift(dots[0].d, -weekdayOf(dots[0].d));
  let i = 0;
  while (i < dots.length) {
    const cells = [];
    const counts = { lit: 0, rest: 0, freeze: 0, quiet: 0, open: 0, pending: 0 };
    for (let wd = 0; wd < 7; wd += 1) {
      const day = shift(monday, wd);
      if (i < dots.length && dots[i].d.getTime() === day.getTime()) {
        cells.push({ state: dots[i].dot.state, title: dots[i].dot.title });
        counts[dots[i].dot.kind] += 1;
        i += 1;
      } else {
        cells.push({ state: 'future', title: `${localDateLabel(day.toISOString().slice(0, 10))}: outside these four weeks` });
      }
    }
    const label = localDateLabel(monday.toISOString().slice(0, 10));
    const parts = [plural(counts.lit, 'day lit', 'days lit')];
    if (counts.rest) parts.push(plural(counts.rest, 'rest day', 'rest days'));
    if (counts.freeze) parts.push(plural(counts.freeze, 'day kept by a freeze', 'days kept by a freeze'));
    if (counts.quiet) parts.push(plural(counts.quiet, 'quiet day', 'quiet days'));
    if (counts.pending) parts.push(plural(counts.pending, 'day not closed yet', 'days not closed yet'));
    if (counts.open) parts.push('today still open');
    weeks.push({ label, summary: `Week of ${label}: ${parts.join(', ')}.`, days: cells });
    monday = shift(monday, 7);
  }
  return weeks;
}

/**
 * `GET /api/streaks/:userId` (or the trophies read's `streak`) → what Today and Trophies show.
 *
 * @returns {null | { current: boolean, header: {count, label}|null, sentence: string, history: object }}
 *   `null` means the answer could not be read.
 */
export function toStreakView(s) {
  if (!isObject(s) || typeof s.verified !== 'boolean') return null;
  const legacy = isObject(s.legacy) ? s.legacy : {};
  if (!s.verified) {
    // Q2: the earlier record, labelled, and no dots it never recorded.
    const n = Number.isInteger(legacy.current) ? legacy.current : null;
    const sentence = n === null
      ? 'Verified streak days are not switched on yet.'
      : `Earlier streak record: ${plural(n, 'day', 'days')} — day rules not verified.`;
    return { current: false, header: null, sentence,
      history: { weeks: [], emptyTitle: 'Verified streak days are not switched on yet', emptyText: n === null ? undefined : sentence } };
  }
  if (s.available !== true) {
    const sentence = 'Streak: not available right now.';
    return { current: false, header: null, sentence, history: { weeks: [], emptyTitle: sentence, emptyText: 'No number is shown until the household’s days can be counted.' } };
  }
  if (!Array.isArray(s.days) || !isObject(s.policy) || !isObject(s.freezes) || !isObject(s.asOf)) return null;
  const weeks = toWeeks(s.days, s.today);
  if (weeks === null) return null;

  if (s.currentRun != null && (!Number.isInteger(s.currentRun) || s.currentRun < 0)) return null;
  const current = s.stale === false && s.pendingCorrection === false && s.blocked === null && Number.isInteger(s.currentRun);
  const every = s.policy.freezeEveryLitDays;
  const through = localDateLabel(s.finalizedThrough);
  const zone = validZone(s.policy.timezone);
  let lead = '';
  let streakLabel;
  let freezeLabel;
  let sentence;
  let header = null;
  if (current) {
    const n = s.currentRun;
    streakLabel = n > 0 ? `${plural(n, 'day', 'days')} lit` : 'No lit days in the current run yet';
    const inv = s.freezes.inventory;
    const cap = s.freezes.capacity;
    freezeLabel = Number.isInteger(inv) && Number.isInteger(cap) && Number.isInteger(every) && every > 0
      ? `${inv} of ${cap} freezes saved — one is earned every ${every} lit days`
      : 'Freezes: not known';
    // Today says the calendar's "today, lit so far" from the same rule the calendar draws its ember
    // with, so the two screens agree: after the closed run's count, or instead of "no lit days".
    const litSoFar = s.days.some((d) => isTodayLitSoFar(d, s.today));
    sentence = n > 0 ? `Streak: ${plural(n, 'day', 'days')} lit${litSoFar ? ', and today is lit so far' : ''}.`
      : litSoFar ? 'Streak: today is lit so far.' : 'Streak: no lit days in the current run yet.';
    if (n > 0) header = { count: n, label: `${plural(n, 'day', 'days')} lit` };
  } else {
    const why = s.pendingCorrection ? 'a correction is being applied'
      : s.blocked ? 'this streak needs a check before it can update'
        : 'recent days are still being closed';
    const last = s.asOf.date && Number.isInteger(s.asOf.run)
      ? ` Last closed record: ${plural(s.asOf.run, 'day', 'days')} lit, as of ${localDateLabel(s.asOf.date)}.` : '';
    // Not `streakLabel`: the card draws a flame beside that one. The sentence opens the note instead.
    lead = `Not current — ${why}.${last} `;
    freezeLabel = 'Freezes: not current';
    sentence = `Streak: not current right now — ${why}.${last}`;
  }
  const rest = s.policy.weekendGrace === true ? ' Weekends are rest days.' : '';
  const clock = zone ? `Days follow the household clock (${zone}). ` : '';
  const note = `${lead}${clock}${through ? `Closed through ${through}.` : 'No day has been closed yet.'}${rest}`;
  return { current, header, sentence,
    history: { streakLabel, freezeLabel, weeks, legend: LEGEND, note,
      emptyTitle: 'No verified days yet', emptyText: 'The first verified day appears here once it begins.' } };
}

const BASIS_WORDS = Object.freeze({
  saved_completions: 'saved quests',
  approved_rewards: 'approved rewards',
});

/** Catalog and receipts use the same JSON criterion object (S6 migration). */
function criterionText(criterion) {
  if (!isObject(criterion)) return 'Criteria not recorded';
  const count = criterion.value ?? criterion.count;
  switch (criterion.type) {
    case 'total_chores': return `${count} approved quests`;
    case 'streak': return `${count} lit days`;
    case 'weekend_chores': return `${count} approved quests on weekends`;
    case 'time_of_day': {
      const before = criterion.before_hour != null;
      const hour = before ? criterion.before_hour : criterion.after_hour;
      return `${count} approved quests ${before ? 'before' : 'from'} ${String(hour).padStart(2, '0')}:00 on the household clock`;
    }
    case 'rapid_completion': return `${count} quests within one hour`;
    default: return 'Criteria not recognized';
  }
}

function correctionText(candle, zone) {
  if (!candle?.correction) return '';
  const reason = candle.correction.note === 'progress_below_target'
    ? 'progress fell below the target; the original award remains in the record'
    : 'the original award record was corrected';
  return `Record corrected ${instantLabel(candle.correction.at, zone)} — ${reason}.`;
}
const goalNumber = (v) => (typeof v === 'number' || (typeof v === 'string' && v.trim() !== ''))
  && Number.isFinite(Number(v));

/**
 * An undo annotated this badge's receipt (S6 `badge_award_receipts.correction_note`). The badge stays
 * earned (design D5, "corrections annotate them"). A correction is written once: its note describes
 * the count when corrected, not today's count or what caused a below-threshold count.
 */
function badgeCorrectionText(correction, threshold, zone) {
  const at = instantLabel(correction.at, zone);
  const lead = `Record corrected${at ? ` ${at}` : ''}`;
  const reason = correction.note === 'below_threshold' ? ` — the count it was earned on was below ${threshold} when corrected.`
    : correction.note === 'source_undone' ? ' — the quest that tipped it was undone; the count still held when corrected.'
      : '.';
  return `${lead}${reason} The badge stays earned.`;
}

/**
 * One earned badge's basis, in words. A legacy badge says so and invents nothing. A corrected receipt
 * never names its completion as the source (#583 D-C): that completion may be the undone one.
 */
function basisText(basis, zone) {
  if (!isObject(basis)) return 'Recorded before sources were kept.';
  const corrected = isObject(basis.correction);
  const parts = [`Earned at ${basis.measuredValue} of ${basis.threshold} (${criterionText(basis.criterion)})`];
  if (basis.sourceType === 'completion') {
    if (!corrected && basis.sourceCompletionId != null) parts.push(`the quest that tipped it is completion #${basis.sourceCompletionId}`);
  } else if (basis.sourceDate) parts.push(`on the verified day ${localDateLabel(basis.sourceDate) ?? basis.sourceDate}`);
  else if (basis.sourceType === 'legacy_streak') parts.push('from the earlier streak record');
  const correction = corrected ? ` ${badgeCorrectionText(basis.correction, basis.threshold, zone)}` : '';
  return `${parts.join('; ')}. Rule ${basis.policyVersion}.${correction}`;
}

function badgeWall(catalog, earnedById, describe) {
  return catalog.filter(isObject).map((a) => {
    const earned = earnedById.get(a.id);
    const date = earned ? instantLabel(earned.earnedAt, describe.timeZone) : null;
    const corrected = isObject(earned?.basis?.correction);
    return {
      id: a.id,
      name: a.name,
      icon: badgeArt(a.icon),
      status: earned ? 'earned' : 'locked',
      statusLabel: corrected ? 'Record corrected' : undefined,
      dateLabel: date ?? undefined,
      // The correction chip replaces the earned/date chip, so retain a known earning date in the story.
      story: earned ? `${a.description ?? ''}${corrected && date ? ` Earned ${date}.` : ''} ${describe.basis(earned)}`.trim() : `${a.description ?? ''} ${criterionText(a.criteria)}. Still waiting for its light.`.trim(),
    };
  });
}

/**
 * `GET /api/trophies/:userId` while on → the Trophies props. `null` for a malformed answer.
 */
export function toLiveTrophies(p, household = null, { includeXp = true } = {}) {
  if (!isObject(p) || p.enabled !== true || !Array.isArray(p.catalog) || !Array.isArray(p.badges)
    || !Array.isArray(p.goals) || !isObject(p.xp) || !isObject(p.coverage)) return null;
  // D5 names an explicit as-of and coverage start, so a missing one is a malformed answer (review M4).
  if (!Number.isFinite(p.xp.credited) || !readableInstant(p.asOf) || !readableInstant(p.coverage.recordedFrom)) return null;
  const streak = toStreakView(p.streak);
  if (!streak) return null;
  const timeZone = screenZone(p.streak, household);
  const earnedById = new Map(p.badges.filter(isObject).map((b) => [b.id, b]));
  const badges = badgeWall(p.catalog, earnedById, { timeZone, basis: (b) => basisText(b.basis, timeZone) });
  const newest = p.badges.find(isObject);
  const newestDef = newest ? p.catalog.find((a) => isObject(a) && a.id === newest.id) : null;

  const goals = [];
  for (const g of p.goals) {
    if (!isObject(g) || !Array.isArray(g.versions)) return null;
    if (g.versions.length === 0) {
      // Saved before goal history was kept (D5's explicit legacy label). No candle, no fill: the
      // earlier loop's completion date is the only thing it recorded.
      if (g.legacyCompletedAt != null && !readableInstant(g.legacyCompletedAt)) return null;
      const done = g.legacyCompletedAt != null ? instantLabel(g.legacyCompletedAt, timeZone, { time: false }) : null;
      goals.push({
        id: g.goalId,
        title: goalTypeTitle(g.goalType),
        value: null,
        target: null,
        format: 'number',
        tone: 'gold',
        candleLabel: 'No candle — an earlier record with no version history',
        chips: [{ label: 'Earlier record — no version history' }],
        note: done
          ? `Completed ${done} under the earlier goal loop. No candle receipt was kept.`
          : 'In progress under the earlier goal loop. No candle receipt yet.',
      });
      continue;
    }
    const currentVersion = g.versions.find((v) => isObject(v) && !v.supersededAt);
    // An edit supersedes and adds a version in one transaction, so every versioned goal has a current one.
    if (!currentVersion) return null;
    if (!goalNumber(currentVersion.target) || Number(currentVersion.target) <= 0
      || !goalNumber(currentVersion.progress) || Number(currentVersion.progress) < 0) return null;
    const target = Number(currentVersion.target);
    const value = Number(currentVersion.progress);
    const goalZone = validZone(currentVersion.timezone) ?? timeZone;
    const correction = correctionText(currentVersion.candle, goalZone);
    const lit = currentVersion.candle && !correction ? `Candle lit ${instantLabel(currentVersion.candle.earnedAt, goalZone) ?? ''}`.trim() : null;
    const earlier = g.versions.filter((v) => isObject(v) && v !== currentVersion && v.candle)
      .map((v) => `version ${v.version} (target ${v.target}) lit ${instantLabel(v.candle.earnedAt, validZone(v.timezone) ?? timeZone) ?? ''}. ${correctionText(v.candle, validZone(v.timezone) ?? timeZone)}`.trim());
    goals.push({
      id: g.goalId,
      title: `${BASIS_WORDS[currentVersion.basis] ? `${BASIS_WORDS[currentVersion.basis][0].toUpperCase()}${BASIS_WORDS[currentVersion.basis].slice(1)}` : 'Goal'}: ${localDateLabel(currentVersion.startDate)} to ${localDateLabel(currentVersion.endDate)}`,
      value,
      target,
      format: 'number',
      tone: 'gold',
      chips: [{ label: `Version ${currentVersion.version}` }, ...(lit ? [{ label: lit, tone: 'moss' }] : []), ...(correction ? [{ label: 'Record corrected' }] : [])],
      note: `${currentVersion.basisLabel ?? ''}.${correction ? ` ${correction}` : ''}${earlier.length ? ` Earlier: ${earlier.join('; ')}.` : ''}`.replace(/^\./, '').trim(),
    });
  }

  const asOf = instantLabel(p.asOf, timeZone);
  const since = instantLabel(p.coverage.recordedFrom, timeZone);
  const notes = [
    ...(includeXp ? [`Story XP credited: ${p.xp.credited}, counted from ${since}.`] : []),
    `As of ${asOf}.`,
  ];
  return {
    latestBadge: newest && newestDef ? { title: `You earned ${newestDef.name} ✦`, story: basisText(newest.basis, timeZone), icon: badgeArt(newestDef.icon) } : null,
    badges,
    goals,
    streakHistory: streak.history,
    streak: streak.header,
    notes,
  };
}

/**
 * The switch is off: what is true today, from the reads that already serve it, with no receipts,
 * no candles and no verified days. `null` for a malformed answer.
 */
export function toLegacyTrophies({ catalog, earned, progress, streak, household = null }) {
  if (!Array.isArray(catalog) || !Array.isArray(earned) || !Array.isArray(progress)) return null;
  const view = toStreakView(streak);
  const history = view?.history ?? { weeks: [], emptyTitle: 'Streak: not available right now.', emptyText: 'The badges and goals are still shown.' };
  const timeZone = screenZone(streak, household);
  const currency = isObject(household) && typeof household.currency === 'string' && /^[A-Z]{3}$/.test(household.currency)
    ? household.currency : null;
  const earnedById = new Map(earned.filter((e) => isObject(e) && e.earned_at).map((e) => [e.id, { earnedAt: e.earned_at }]));
  const badges = badgeWall(catalog, earnedById, { timeZone, basis: () => 'Source detail not recorded.' });
  const goals = progress.filter(isObject).map((g) => {
    // `goal.service.js` counts quests for the quest types and SUMS reward money for the others.
    const quests = QUEST_COUNT_GOAL_TYPES.has(g.goalType);
    const money = !quests && currency !== null;
    return {
      id: g.goalId,
      title: goalTypeTitle(g.goalType),
      value: Number(g.currentValue) || 0,
      target: Number(g.targetValue) || 0,
      percent: Number.isFinite(Number(g.percentage)) ? Number(g.percentage) : undefined,
      format: money ? 'currency' : 'number',
      ...(money ? { currency } : {}),
      ...(quests ? { unitLabel: 'quests' } : {}),
      tone: 'gold',
      chips: [{ label: g.isCompleted ? 'Reached' : 'In progress' }],
      note: quests || money
        ? 'Progress as the goal screen counts it today, not a lit candle.'
        : 'Progress as the goal screen counts it today, not a lit candle. An amount of money; the household’s currency is not known here.',
    };
  });
  return {
    latestBadge: null,
    badges,
    goals,
    streakHistory: history,
    streak: view?.header ?? null,
    notes: ['The trophy story view is not switched on yet, so no badge sources or goal candle receipts are shown. Streak availability is reported separately. The badges and goals below are the ones recorded today.'],
  };
}

/** Said when the level read failed or could not be read. Never level 1 standing in for unknown. */
export const STORY_LEVEL_UNAVAILABLE = 'Story level: not available right now.';

const whole = (n) => Number.isSafeInteger(n) && n >= 0;

/**
 * `GET /api/story-progress/:userId` (S13) → the one secondary line Today and Trophies show.
 *
 * The server owns the Q3 curve; this reads what it said and refuses an answer whose numbers do not add up.
 * `{ off: true }` while the switch is off (show nothing); `null` for anything unreadable (say "not available").
 */
export function toStoryLevelView(p, timeZone = null) {
  if (isObject(p) && p.enabled === false) return { off: true, sentence: null };
  if (!isObject(p) || p.enabled !== true || !isObject(p.level) || !isObject(p.policy) || !isObject(p.coverage)) return null;
  const { level, levelStartXp, nextLevelXp, xpIntoLevel, stepXp, xpToNext } = p.level;
  if (![p.xp, levelStartXp, nextLevelXp, xpIntoLevel, stepXp, xpToNext].every(whole) || !whole(level) || level < 1) return null;
  if (levelStartXp + xpIntoLevel !== p.xp || xpIntoLevel + xpToNext !== stepXp || levelStartXp + stepXp !== nextLevelXp || xpToNext < 1) return null;
  if (typeof p.policy.version !== 'string' || p.policy.version === '' || typeof p.coverage.earlierWork !== 'boolean') return null;
  const since = readableInstant(p.coverage.from) ? instantLabel(p.coverage.from, timeZone) : null;
  if (!since) return null;
  const earlier = p.coverage.earlierWork ? '; quests saved before then are not counted' : '';
  return {
    off: false,
    sentence: `Story level ${level} — ${xpIntoLevel} of ${stepXp} XP toward level ${level + 1}. ${p.xp} XP counted since ${since}${earlier}.`,
  };
}
